// src/pages/AnalyzePage.jsx — VIRAJ Campaign Failure Analyzer
// Entry point. One job: CSV → Incident Report.
// States: upload → (mapping?) → loading → result | no_anomaly | error

import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../api/workerClient";
import CrisisHighlight from "../components/dashboard/CrisisHighlight";
import FinanceChartV10 from "../components/dashboard/FinanceChartV10";
import TruncateLargeFileBanner from "../components/ui/TruncateLargeFileBanner";
import { ROOT_CAUSE_LABELS } from "../constants/rootCauseLabels";
import {
  ArchetypeBadge,
  ConfidenceBadge,
  ForensicNarrative,
  TriggerHypothesis,
  WhatToCheck,
  TimelineAnchors,
} from "../components/forensic/forensic_ui_components";

const MONO = "var(--font-mono)";
const BTN_PRI = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "10px 20px",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  fontFamily: MONO,
  fontSize: 12,
  fontWeight: 700,
  border: "none",
  background: "var(--accent-primary)",
  color: "#fff",
};
const BTN_SEC = {
  ...BTN_PRI,
  background: "var(--bg-elevated)",
  color: "var(--text-secondary)",
};

// ── Tracker presets ───────────────────────────────────────────────────────────
const TRACKER_PRESETS = [
  { id: "auto", label: "Auto", mapping: null },
  {
    id: "keitaro",
    label: "Keitaro",
    mapping: {
      dateCol: "date",
      spendCol: "cost",
      revenueCol: "revenue",
    },
  },
  {
    id: "binom",
    label: "Binom",
    mapping: { dateCol: "date", spendCol: "spend", revenueCol: "revenue" },
  },
  {
    id: "voluum",
    label: "Voluum",
    mapping: {
      dateCol: "date",
      spendCol: "cost",
      revenueCol: "conversions_value",
    },
  },
  {
    id: "meta",
    label: "Meta Ads",
    mapping: {
      dateCol: "Day",
      spendCol: "Amount spent (USD)",
      revenueCol: "Website purchases conversion value (USD)",
    },
  },
  {
    id: "google",
    label: "Google",
    mapping: { dateCol: "Day", spendCol: "Cost", revenueCol: "Conv. value" },
  },
  {
    id: "redtrack",
    label: "RedTrack",
    mapping: { dateCol: "Date", spendCol: "Cost", revenueCol: "Revenue" },
  },
  {
    id: "tiktok",
    label: "TikTok",
    mapping: {
      dateCol: "Date",
      spendCol: "Cost",
      revenueCol: "Conversion value",
    },
  },
  { id: "custom", label: "+ Other", mapping: null },
];

// ── Demo files ────────────────────────────────────────────────────────────────
const DEMO_FILES = [
  {
    label: "Conversion collapse",
    file: "/demo/demo_crisis.csv",
    description: "Campaign with a performance drop",
  },
  {
    label: "Normal campaign",
    file: "/demo/demo_stable.csv",
    description: "No incidents detected",
  },
  {
    label: "Crisis + recovery",
    file: "/demo/demo_recovery.csv",
    description: "Drop followed by recovery",
  },
];

// ── API call ──────────────────────────────────────────────────────────────────
async function runAnalysis(file, manualMapping, manualBaseline, isDemo = false) {
  const formData = new FormData();
  formData.append("file", file);
  if (manualMapping)
    formData.append("manual_mapping", JSON.stringify(manualMapping));
  if (manualBaseline)
    formData.append("manual_baseline", JSON.stringify(manualBaseline));
  const endpoint = isDemo ? `${BASE_URL}/api/analyze?demo=true` : `${BASE_URL}/api/analyze`;
  const res = await fetch(endpoint, {
    method: "POST",
    body: formData,
  });
  return res.json();
}

// ── Truncate CSV ──────────────────────────────────────────────────────────────
function truncateCsvFile(file, maxRows) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split(/\r?\n/);
      const trimmed = [
        lines[0],
        ...lines.slice(1).filter(Boolean).slice(0, maxRows),
      ].join("\n");
      resolve(new File([trimmed], file.name, { type: "text/csv" }));
    };
    reader.readAsText(file);
  });
}

// ══════════════════════════════════════════════════════════════════════════════
// FORENSIC COMPONENTS (kept for use in ResultState)
// These are re-exported from forensic_ui_components, but we keep the local
// definitions for UploadState and MappingState (they are not changed).
// ══════════════════════════════════════════════════════════════════════════════

// Archetype badge is imported, no need to redefine.
// Confidence badge is imported.
// ForensicNarrative imported.
// TriggerHypothesis imported.
// WhatToCheck imported.
// TimelineAnchors imported.

// ── CSV Export Hint ───────────────────────────────────────────────────────────
function CsvExportHint() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--border-subtle)", overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "9px 14px", background: "var(--bg-elevated)", border: "none", cursor: "pointer",
          fontFamily: MONO, fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.08em",
        }}
      >
        <span>How to export your Google Ads CSV</span>
        <span style={{ fontSize: 9 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ padding: "12px 14px", background: "var(--bg-card)", borderTop: "1px solid var(--border-subtle)" }}>
          <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-secondary)", lineHeight: 2 }}>
            Google Ads → Reports → Predefined reports → Campaign<br />
            → Select date range (90+ days recommended) → Download CSV
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STATE: UPLOAD (unchanged)
// ══════════════════════════════════════════════════════════════════════════════
function UploadState({ onFile, onDemo }) {
  const [dragging, setDragging] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("auto");
  const [demoHover, setDemoHover] = useState(false);
  const fileRef = useRef(null);

  const primaryDemo = DEMO_FILES[0];

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      const f = e.dataTransfer.files[0];
      if (f?.name.endsWith(".csv")) {
        const mapping =
          TRACKER_PRESETS.find((p) => p.id === selectedPreset)?.mapping ?? null;
        onFile(f, mapping);
      }
    },
    [onFile, selectedPreset]
  );

  function handleBrowse(e) {
    const f = e.target.files[0];
    if (f) {
      const mapping =
        TRACKER_PRESETS.find((p) => p.id === selectedPreset)?.mapping ?? null;
      onFile(f, mapping);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
        maxWidth: 560,
        margin: "40px auto",
      }}
    >
      {/* Header */}
      <div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 22,
            fontWeight: 700,
            color: "var(--text-primary)",
            marginBottom: 6,
          }}
        >
          Forensic Failure Reconstruction
        </div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 12,
            color: "var(--text-muted)",
            lineHeight: 1.6,
          }}
        >
          See exactly when your campaign broke, why it happened, and how much
          was avoidable.
        </div>
      </div>

      {/* Privacy note */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          padding: "10px 14px",
          borderRadius: "var(--radius-sm)",
          background: "var(--bg-elevated)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <span style={{ fontSize: 14, marginTop: 1 }}>🔒</span>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 10,
            color: "var(--text-secondary)",
            lineHeight: 1.6,
          }}
        >
          VIRAJ works locally. Your data never leaves your computer and is never
          modified — we analyse a copy in memory.
        </div>
      </div>

      {/* PRIMARY: Demo button */}
      <div
        style={{
          padding: "20px 24px",
          borderRadius: "var(--radius)",
          background: "var(--accent-faint)",
          border: "1px solid var(--accent-primary)",
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: 10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--accent-primary)",
            marginBottom: 10,
            fontWeight: 700,
          }}
        >
          See it work first — no file needed
        </div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 12,
            color: "var(--text-secondary)",
            marginBottom: 14,
            lineHeight: 1.6,
          }}
        >
          Run the demo analysis on a real campaign failure. See the exact moment
          performance broke and the estimated loss — then decide if it's worth
          checking your own data.
        </div>
        <button
          style={{
            ...BTN_PRI,
            width: "100%",
            justifyContent: "center",
            padding: "13px 20px",
            fontSize: 13,
            background: demoHover ? "#60A5FA" : "#3B82F6",
            boxShadow: demoHover
              ? "0 0 18px 3px rgba(96,165,250,0.45)"
              : "0 0 0px 0px rgba(96,165,250,0)",
            transition: "background 0.18s, box-shadow 0.22s",
            outline: "none",
            border: "none",
          }}
          onMouseEnter={() => setDemoHover(true)}
          onMouseLeave={() => setDemoHover(false)}
          onClick={() => onDemo(primaryDemo.file, primaryDemo.label)}
        >
          ▶ Run Demo Analysis — see how it works
        </button>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: "var(--text-muted)",
            textAlign: "center",
            marginTop: 8,
          }}
        >
          Conversion collapse scenario · takes ~3 seconds
        </div>
      </div>

      {/* Divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{ flex: 1, height: 1, background: "var(--border-subtle)" }}
        />
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: "var(--text-muted)",
            letterSpacing: "0.1em",
          }}
        >
          OR ANALYSE YOUR OWN DATA
        </div>
        <div
          style={{ flex: 1, height: 1, background: "var(--border-subtle)" }}
        />
      </div>

      {/* Tracker selector */}
      <div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--text-muted)",
            marginBottom: 8,
          }}
        >
          Select your tracker (optional — Auto works for most exports)
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {TRACKER_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedPreset(p.id)}
              style={{
                padding: "5px 11px",
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                fontFamily: MONO,
                fontSize: 10,
                fontWeight: selectedPreset === p.id ? 700 : 400,
                border: `1px solid ${
                  selectedPreset === p.id
                    ? "var(--accent-primary)"
                    : "var(--border-subtle)"
                }`,
                background:
                  selectedPreset === p.id
                    ? "var(--accent-faint)"
                    : "var(--bg-elevated)",
                color:
                  selectedPreset === p.id
                    ? "var(--accent-primary)"
                    : "var(--text-secondary)",
                transition: "all 0.12s",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        {selectedPreset !== "auto" && selectedPreset !== "custom" && (
          <div
            style={{
              fontFamily: MONO,
              fontSize: 9,
              color: "var(--success)",
              marginTop: 6,
            }}
          >
            ✓{" "}
            {TRACKER_PRESETS.find((p) => p.id === selectedPreset)?.label} format
            selected
          </div>
        )}
      </div>

      {/* Onboarding copy */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.7 }}>
          Upload your campaign CSV to find out what broke and when.
        </div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-muted)", lineHeight: 1.6 }}>
          Supports: Google Ads CSV export · Keitaro · Voluum · Binom · Meta Ads · TikTok
        </div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-muted)", lineHeight: 1.6 }}>
          What you get: root cause · timeline · estimated loss
        </div>
      </div>

      {/* SECONDARY: Upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          padding: "32px 30px",
          borderRadius: "var(--radius)",
          border: `2px dashed ${
            dragging ? "var(--accent-primary)" : "var(--border-subtle)"
          }`,
          background: dragging ? "var(--accent-faint)" : "var(--bg-card)",
          cursor: "pointer",
          textAlign: "center",
          transition: "all 0.15s",
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleBrowse}
        />
        <div
          style={{
            fontFamily: MONO,
            fontSize: 24,
            marginBottom: 10,
          }}
        >
          ⊕
        </div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 13,
            color: "var(--text-primary)",
            marginBottom: 6,
          }}
        >
          Drop your CSV here or click to browse
        </div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 10,
            color: "var(--text-muted)",
            lineHeight: 1.6,
          }}
        >
          Any tracker export works — we find the columns automatically.
          <br />
          You don't need to rename anything.
        </div>
      </div>

      {/* How to export CSV — collapsible */}
      <CsvExportHint />

      {/* Microcopy */}
      <div
        style={{
          fontFamily: MONO,
          fontSize: 10,
          color: "var(--text-muted)",
          textAlign: "center",
        }}
      >
        Upload your tracker CSV — VIRAJ finds anomalies automatically.
      </div>

      {/* Format hint */}
      <div
        style={{
          fontFamily: MONO,
          fontSize: 10,
          color: "var(--text-muted)",
          textAlign: "center",
          lineHeight: 1.8,
        }}
      >
        Needs at least: <span style={{ color: "var(--text-secondary)" }}>
          date · spend · revenue
        </span>{" "}
        —{" "}
        <a
          href="/demo/template.csv"
          download
          style={{ color: "var(--accent-primary)", textDecoration: "none" }}
        >
          download template
        </a>
        <br />
        <span style={{ opacity: 0.6 }}>
          Other columns are ignored. Names don't matter.
        </span>
      </div>

      {/* Other demo files */}
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {DEMO_FILES.slice(1).map((d) => (
          <button
            key={d.file}
            style={{ ...BTN_SEC, fontSize: 10, padding: "6px 12px" }}
            onClick={() => onDemo(d.file, d.label)}
          >
            {d.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STATE: COLUMN MAPPING (unchanged)
// ══════════════════════════════════════════════════════════════════════════════
function MappingState({ columns, onConfirm, onBack }) {
  const [dateCol, setDateCol] = useState(columns[0] ?? "");
  const [spendCol, setSpendCol] = useState(columns[1] ?? "");
  const [revenueCol, setRevenueCol] = useState(columns[2] ?? "");
  const [customCR, setCustomCR] = useState("");
  const [err, setErr] = useState("");

  function confirm() {
    if (!dateCol || !spendCol || !revenueCol) {
      setErr("Please select all three columns");
      return;
    }
    const manualBaseline = customCR ? { cr: parseFloat(customCR) / 100 } : undefined;
    onConfirm({ dateCol, spendCol, revenueCol }, manualBaseline);
  }

  const fields = [
    {
      label: "Which column is the date?",
      hint: "e.g. Day, Date, Report date",
      value: dateCol,
      set: setDateCol,
    },
    {
      label: "Which column is your ad spend?",
      hint: "e.g. Cost, Spend, Amount spent",
      value: spendCol,
      set: setSpendCol,
    },
    {
      label: "Which column is your revenue?",
      hint: "e.g. Revenue, Payout, Conv. value",
      value: revenueCol,
      set: setRevenueCol,
    },
  ];

  return (
    <div
      style={{
        maxWidth: 480,
        margin: "40px auto",
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      <div
        style={{
          fontFamily: MONO,
          fontSize: 14,
          fontWeight: 700,
          color: "var(--text-primary)",
        }}
      >
        Help us read your CSV
      </div>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 11,
          color: "var(--text-secondary)",
          lineHeight: 1.6,
        }}
      >
        Your file uses custom column names. Just tell us which column is which —
        takes 10 seconds.
      </div>

      {fields.map(({ label, hint, value, set }) => (
        <div key={label}>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 11,
              color: "var(--text-secondary)",
              marginBottom: 4,
              fontWeight: 600,
            }}
          >
            {label}
          </div>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 9,
              color: "var(--text-muted)",
              marginBottom: 5,
            }}
          >
            {hint}
          </div>
          <select
            value={value}
            onChange={(e) => set(e.target.value)}
            style={{
              width: "100%",
              fontFamily: MONO,
              fontSize: 12,
              background: "var(--bg-base)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-sm)",
              padding: "8px 10px",
              color: "var(--text-primary)",
            }}
          >
            <option value="">— select —</option>
            {columns.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
      ))}

      <div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 11,
            color: "var(--text-secondary)",
            marginBottom: 4,
            fontWeight: 600,
          }}
        >
          Your typical conversion rate % (optional)
        </div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: "var(--text-muted)",
            marginBottom: 5,
          }}
        >
          Not sure? Leave blank — VIRAJ will estimate it from your data.
        </div>
        <input
          type="number"
          value={customCR}
          onChange={(e) => setCustomCR(e.target.value)}
          placeholder="e.g. 3 (for 3%)"
          step="0.1"
          min="0.1"
          max="50"
          style={{
            width: "100%",
            fontFamily: MONO,
            fontSize: 12,
            background: "var(--bg-base)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-sm)",
            padding: "8px 10px",
            color: "var(--text-primary)",
          }}
        />
      </div>

      {err && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: 11,
            color: "var(--danger)",
          }}
        >
          {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button style={BTN_PRI} onClick={confirm}>
          Analyze →
        </button>
        <button style={BTN_SEC} onClick={onBack}>
          ← Back
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STATE: RESULT — PATCHED VERSION
// ══════════════════════════════════════════════════════════════════════════════
function ResultState({ result, fileName, onReset, navigate }) {
  const [copied, setCopied] = useState(false);

  function handleCopySummary() {
    const { narrative, attribution, stats, forensic } = result;
    const cause =
      ROOT_CAUSE_LABELS[attribution?.root_cause] ??
      attribution?.root_cause ??
      "Unknown";
    const text = [
      `VIRAJ Campaign Failure Report — ${fileName}`,
      "",
      `Failure type: ${forensic?.archetype?.label ?? "—"}`,
      `Failure started: ${
        forensic?.timeline_anchors?.degradation_start ??
        stats?.crisis_start ??
        "—"
      }`,
      `Root cause: ${cause}`,
      `Money lost: €${(stats?.estimated_loss ?? 0).toLocaleString("en-US")}`,
      "",
      forensic?.narrative_forensic ?? narrative?.summary ?? "",
      "",
      forensic?.trigger_hypothesis?.primary?.text
        ? `Most likely cause (${
            forensic.trigger_hypothesis.primary.pct ?? "?"
          }%): ${forensic.trigger_hypothesis.primary.text}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  // No anomaly result
  if (result.no_anomaly) {
    return (
      <div
        style={{
          maxWidth: 680,
          margin: "32px auto",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: "var(--text-muted)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          Analysis complete · {fileName}
        </div>
        <div
          style={{
            padding: "20px",
            borderRadius: "var(--radius)",
            background: "var(--success-bg)",
            border: "1px solid var(--success-border)",
          }}
        >
          <div
            style={{
              fontFamily: MONO,
              fontSize: 14,
              fontWeight: 700,
              color: "var(--success)",
              marginBottom: 8,
            }}
          >
            ✓ No incidents found
          </div>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 12,
              color: "var(--text-secondary)",
              lineHeight: 1.6,
            }}
          >
            This campaign ran within normal performance range across all{" "}
            {result.stats?.days_total} days. No significant drops or anomalies
            were detected.
          </div>
          {result.tip && (
            <div
              style={{
                fontFamily: MONO,
                fontSize: 10,
                color: "var(--text-muted)",
                marginTop: 10,
              }}
            >
              Tip: {result.tip}
            </div>
          )}
        </div>
        {result.timeline?.length > 0 && (
          <div
            style={{
              padding: "16px",
              borderRadius: "var(--radius)",
              background: "var(--bg-card)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <div
              style={{
                fontFamily: MONO,
                fontSize: 9,
                color: "var(--text-muted)",
                marginBottom: 12,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Performance over time — all within normal range
            </div>
            <FinanceChartV10
              data={result.timeline.map((t) => ({
                date: t.date,
                spend: t.spend,
                revenue: t.revenue,
                roi: t.roi,
                risk_status: t.risk,
              }))}
              riskHistory={[]}
              actions={[]}
              loading={false}
            />
          </div>
        )}
        <button style={BTN_SEC} onClick={onReset}>
          ← Analyze another campaign
        </button>
      </div>
    );
  }

  // Incident result
  const { narrative, attribution, risk, stats, timeline, forensic } = result;

  const sevColor =
    risk?.status === "critical" || risk?.status === "high"
      ? "var(--danger)"
      : "var(--warning)";

  const chartData = (timeline ?? []).map((t) => ({
    date: t.date,
    spend: t.spend,
    revenue: t.revenue,
    roi: t.roi,
    risk_status: t.anomaly
      ? t.risk === "critical"
        ? "critical"
        : "high"
      : "low",
  }));
  const riskHistory = (timeline ?? []).map((t) => ({
    date: t.date,
    risk_status: t.risk,
    roi: t.roi,
    spend: t.spend,
    revenue: t.revenue,
  }));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
        maxWidth: 720,
        margin: "0 auto",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: "var(--text-muted)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          {fileName} · {stats?.days_total} days analyzed
        </div>
        <button
          style={{ ...BTN_SEC, padding: "4px 10px", fontSize: 10 }}
          onClick={onReset}
        >
          ← New analysis
        </button>
      </div>

      {/* Summary card */}
      <div
        style={{
          padding: "20px 24px",
          borderRadius: "var(--radius)",
          background: "var(--bg-card)",
          border: `1px solid ${sevColor}30`,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* Archetype badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {forensic?.archetype && <ArchetypeBadge archetype={forensic.archetype} />}
        </div>

        {/* Timeline anchors */}
        {forensic?.timeline_anchors && (
          <TimelineAnchors timeline_anchors={forensic.timeline_anchors} />
        )}

        {/* Money lost */}
        {stats?.estimated_loss > 0 && (
          <div>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 9,
                color: "var(--text-muted)",
                marginBottom: 4,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Money lost
            </div>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 28,
                fontWeight: 700,
                color: "var(--danger)",
              }}
            >
              € {(stats.estimated_loss).toLocaleString("en-US")}
            </div>
          </div>
        )}

        {/* Confidence */}
        {forensic?.confidence_output && (
          <ConfidenceBadge confidence_output={forensic.confidence_output} />
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 8 }}>
          <button style={BTN_SEC} onClick={handleCopySummary}>
            {copied ? "✓ Copied" : "Copy summary"}
          </button>
          <button style={BTN_SEC} onClick={() => window.print()}>
            Print report
          </button>
        </div>
      </div>

      {/* What happened + Most likely cause + Where to look first */}
      <div
        style={{
          padding: "18px 22px",
          borderRadius: "var(--radius)",
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* What happened */}
        {forensic?.narrative_forensic ? (
          <ForensicNarrative forensic={forensic} />
        ) : (
          <>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 9,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--text-muted)",
              }}
            >
              What happened
            </div>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 12,
                color: "var(--text-secondary)",
                lineHeight: 1.7,
              }}
            >
              {narrative?.summary}
            </div>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 11,
                color: "var(--text-muted)",
                lineHeight: 1.65,
              }}
            >
              {narrative?.details}
            </div>
          </>
        )}

        {/* Most likely cause — corpus-backed, ranked */}
        {forensic?.trigger_hypothesis && (
          <TriggerHypothesis trigger_hypothesis={forensic.trigger_hypothesis} />
        )}

        {/* Where to look first — action steps */}
        {forensic?.what_to_check && (
          <WhatToCheck what_to_check={forensic.what_to_check} />
        )}
      </div>

      {/* Chart */}
      <div
        style={{
          padding: "16px",
          borderRadius: "var(--radius)",
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: "var(--text-muted)",
            marginBottom: 12,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          Performance over time
        </div>
        <FinanceChartV10 data={chartData} riskHistory={riskHistory} />
      </div>

      {/* Crisis highlight */}
      <CrisisHighlight riskHistory={riskHistory} loading={false} />

      {/* CTA — next step */}
      <div style={{
        padding: "20px 24px",
        borderRadius: "var(--radius)",
        background: "var(--accent-faint)",
        border: "1px solid var(--accent-primary)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
          Want to know what to do next?
        </div>
        <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.6 }}>
          This pattern has a specific fix sequence. Send us your data — we'll walk through it within 24 hours.
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a
            href={"mailto:garrytret@gmail.com?subject=VIRAJ Audit Request — " + encodeURIComponent(fileName)}
            style={{ ...BTN_PRI, textDecoration: "none", fontSize: 11 }}
          >
            Email us →
          </a>
          <a
            href="mailto:garrytret@gmail.com?subject=Full Audit — €49"
            style={{ ...BTN_SEC, textDecoration: "none", fontSize: 11 }}
          >
            Get full audit — €49 →
          </a>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function AnalyzePage() {
  const navigate = useNavigate();
  const [state, setState] = useState("upload");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [mappingData, setMappingData] = useState(null);
  const [fileName, setFileName] = useState("");

  async function processFile(f, manualMapping, manualBaseline, isDemo = false) {
    setState("loading");
    setError("");
    try {
      const data = await runAnalysis(f, manualMapping, manualBaseline, isDemo);

      if (!data.ok) {
        if (data.needs_mapping) {
          setMappingData({ columns: data.available_columns });
          setState("mapping");
          return;
        }
        if (data.error === "large_file") {
          setState("upload");
          setError(
            `File has ${data.rows_detected} rows. Max ${data.max_rows} for instant analysis.`
          );
          setMappingData({
            original_rows: data.rows_detected,
            max_rows: data.max_rows,
            original_file: f,
          });
          return;
        }
        setError(data.message ?? "Analysis failed");
        setState("error");
        return;
      }

      setResult(data);
      setState("result");
    } catch (e) {
      setError(String(e));
      setState("error");
    }
  }

  function handleFile(f, presetMapping) {
    setFile(f);
    setFileName(f.name);
    processFile(f, presetMapping, null);
  }

  async function handleDemo(url, label) {
    setState("loading");
    setFileName(label);
    try {
      const resp = await fetch(url);
      const text = await resp.text();
      const demoFile = new File([text], `${label}.csv`, { type: "text/csv" });
      setFile(demoFile);
      processFile(demoFile, null, null, true);
    } catch {
      setError("Could not load demo file");
      setState("error");
    }
  }

  function handleMappingConfirm(manualMapping, manualBaseline) {
    processFile(file, manualMapping, manualBaseline);
  }

  async function handleTruncate() {
    const src = mappingData?.original_file;
    if (!src) return;
    const trimmed = await truncateCsvFile(src, mappingData.max_rows ?? 500);
    setFileName(fileName + " (first 500 rows)");
    processFile(trimmed, null, null);
  }

  function reset() {
    setState("upload");
    setFile(null);
    setResult(null);
    setError("");
    setMappingData(null);
    setFileName("");
  }

  return (
    <div style={{ padding: "24px 16px" }}>
      {state === "upload" && (
        <>
          <UploadState onFile={handleFile} onDemo={handleDemo} />
          {error && !mappingData?.original_rows && (
            <div
              style={{
                maxWidth: 560,
                margin: "0 auto",
                marginTop: 12,
              }}
            >
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--danger-bg)",
                  border: "1px solid var(--danger-border)",
                  fontFamily: MONO,
                  fontSize: 11,
                  color: "var(--danger)",
                }}
              >
                {error}
              </div>
            </div>
          )}
          {mappingData?.original_rows && (
            <div style={{ maxWidth: 560, margin: "12px auto 0" }}>
              <TruncateLargeFileBanner
                rowsDetected={mappingData.original_rows}
                maxRows={mappingData.max_rows}
                onTruncate={handleTruncate}
                onCancel={reset}
              />
            </div>
          )}
        </>
      )}

      {state === "mapping" && mappingData?.columns && (
        <MappingState
          columns={mappingData.columns}
          onConfirm={handleMappingConfirm}
          onBack={reset}
        />
      )}

      {state === "loading" && (
        <div
          style={{
            maxWidth: 560,
            margin: "80px auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: MONO,
              fontSize: 13,
              color: "var(--text-secondary)",
              marginBottom: 12,
            }}
          >
            Analyzing {fileName}...
          </div>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 10,
              color: "var(--text-muted)",
              lineHeight: 1.8,
            }}
          >
            Finding when performance started to change
            <br />
            Identifying the most likely cause
            <br />
            Calculating estimated impact
          </div>
        </div>
      )}

      {state === "result" && result && (
        <ResultState
          result={result}
          fileName={fileName}
          onReset={reset}
          navigate={navigate}
        />
      )}

      {state === "error" && (
        <div
          style={{
            maxWidth: 480,
            margin: "60px auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: MONO,
              fontSize: 22,
              marginBottom: 12,
            }}
          >
            ⚠
          </div>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 13,
              color: "var(--text-primary)",
              marginBottom: 8,
              fontWeight: 700,
            }}
          >
            Something went wrong
          </div>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 11,
              color: "var(--text-muted)",
              marginBottom: 16,
              lineHeight: 1.6,
            }}
          >
            {error}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button style={BTN_PRI} onClick={reset}>
              Try again
            </button>
            <a
              href="/demo/template.csv"
              download
              style={{ ...BTN_SEC, textDecoration: "none" }}
            >
              Download example CSV
            </a>
          </div>
        </div>
      )}
    </div>
  );
} 